import React, { useState, useEffect } from 'react';
import BottomSheet from '../../../shared/ui/forms/BottomSheet';
import UserList, { User } from '../../../shared/ui/lists/UserList';
import { sitesV2Api, SiteMember, SiteMemberRole } from '../../../api/sitesV2Api';
import config from '../../../config/config';
import './PostParticipantsBottomSheet.css';

interface PostParticipantsBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  siteId?: number | null;
}

type Category = 'members' | 'suppliers';

const PostParticipantsBottomSheet: React.FC<PostParticipantsBottomSheetProps> = ({
  isOpen,
  onClose,
  siteId
}) => {
  const [activeCategory, setActiveCategory] = useState<Category>('members');
  const [members, setMembers] = useState<SiteMember[]>([]);
  const [suppliers, setSuppliers] = useState<SiteMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && siteId) {
      loadData();
    } else {
      // Сбрасываем данные при закрытии
      setMembers([]);
      setSuppliers([]);
      setError(null);
    }
  }, [isOpen, siteId]);

  const loadData = async () => {
    if (!siteId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Загружаем всех участников объекта
      const allMembers = await sitesV2Api.getSiteMembers(siteId);
      const activeMembers = allMembers.filter(m => m.isActive);
      
      // Разделяем на участников объекта и поставщиков
      // Поставщики - это участники с ролью, содержащей "Поставщик" или "поставщик"
      const supplierRoleKeywords = ['поставщик', 'supplier'];
      
      const membersList: SiteMember[] = [];
      const suppliersList: SiteMember[] = [];
      
      activeMembers.forEach(member => {
        // Проверяем, есть ли у участника роль поставщика
        const hasSupplierRole = member.roles && member.roles.length > 0
          ? member.roles
              .filter(r => r.isActive)
              .some(r => {
                const roleName = (r.roleName || '').toLowerCase();
                return supplierRoleKeywords.some(keyword => roleName.includes(keyword.toLowerCase()));
              })
          : false;
        
        // Также проверяем старую роль (deprecated)
        const oldRoleName = (member.roleName || '').toLowerCase();
        const hasOldSupplierRole = supplierRoleKeywords.some(keyword => oldRoleName.includes(keyword.toLowerCase()));
        
        if (hasSupplierRole || hasOldSupplierRole) {
          suppliersList.push(member);
        } else {
          membersList.push(member);
        }
      });
      
      setMembers(membersList);
      setSuppliers(suppliersList);
    } catch (err) {
      console.error('Error loading participants:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  // Преобразуем SiteMember[] в User[] для UserList
  const convertMembersToUsers = (membersData: SiteMember[]): User[] => {
    const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
    return membersData.map(member => ({
      userId: member.userId.toString(),
      userName: member.userName || `Пользователь ${member.userId}`,
      roles: member.roles && member.roles.length > 0 
        ? member.roles.filter((r: SiteMemberRole) => r.isActive).map((r: SiteMemberRole) => ({ 
            id: r.id, 
            roleName: r.roleName, 
            roleId: r.roleId 
          }))
        : undefined,
      userRole: member.roleName || 'Участник',
      avatarUrl: member.avatarUrl 
        ? (member.avatarUrl.startsWith('http') ? member.avatarUrl : `${apiBaseUrl}${member.avatarUrl}`)
        : undefined
    }));
  };

  const currentData = activeCategory === 'members' ? members : suppliers;
  const users = convertMembersToUsers(currentData);

  if (!siteId) {
    return null;
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Участники"
      size="large"
    >
      <div className="post-participants-bottom-sheet">
        {/* Табы категорий */}
        <div className="participants-tabs">
          <button
            className={`participants-tab ${activeCategory === 'members' ? 'active' : ''}`}
            onClick={() => setActiveCategory('members')}
          >
            <i className="ri-group-line"></i>
            <span>Участники объекта</span>
          </button>
          <button
            className={`participants-tab ${activeCategory === 'suppliers' ? 'active' : ''}`}
            onClick={() => setActiveCategory('suppliers')}
          >
            <i className="ri-store-line"></i>
            <span>Поставщики</span>
          </button>
        </div>

        {/* Контент */}
        <div className="participants-content">
          {loading ? (
            <div className="participants-loading">
              <i className="ri-loader-4-line"></i>
              <span>Загрузка...</span>
            </div>
          ) : error ? (
            <div className="participants-error">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          ) : users.length === 0 ? (
            <div className="participants-empty">
              <i className={activeCategory === 'members' ? 'ri-group-line' : 'ri-store-line'}></i>
              <span>
                {activeCategory === 'members' 
                  ? 'Нет участников объекта' 
                  : 'Нет поставщиков'}
              </span>
            </div>
          ) : (
            <UserList
              users={users}
              showRemoveButton={false}
            />
          )}
        </div>
      </div>
    </BottomSheet>
  );
};

export default PostParticipantsBottomSheet;
